import { Facebook, Twitter, Linkedin, Instagram } from 'lucide-react';
import { Link } from 'wouter';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { useState } from 'react';
import { useToast } from '@/hooks/use-toast';
import { subscribeNewsletter } from '@/lib/api/newsletter';

export function SiteFooter() {
  const [email, setEmail] = useState('');
  const { toast } = useToast();

  // Data for social icons
  const socialIcons = [
    // { href: '#', src: MessageCircle, alt: 'Direct Message' },
    {
      href: 'https://www.instagram.com/legisiq/',
      src: Instagram,
      alt: 'Instagram',
    },
    {
      href: 'https://www.facebook.com/legisiq',
      src: Facebook,
      alt: 'Facebook',
    },
    { href: 'https://x.com/legisiq', src: Twitter, alt: 'Twitter' },
    {
      href: 'https://www.linkedin.com/company/legisiq',
      src: Linkedin,
      alt: 'LinkedIn',
    },
  ];

  // Footer navigation links
  const footerLinks = [
    {
      title: 'Product',
      links: [{ label: 'Features', href: '/features' }],
    },
    {
      title: 'Agents',
      links: [
        { label: 'Bill Agent', href: '/products/bill-agent' },
        { label: 'Committee Agent', href: '/products/committee-agent' },
      ],
    },
    {
      title: 'Portals',
      links: [
        { label: 'Bills', href: '/portals/bills' },
        { label: 'Members', href: '/portals/members' },
        { label: 'Committees', href: '/portals/committees' },
        { label: 'Transcript Hub', href: '/portals/transcript-hub' },
        { label: 'Lobby', href: '/portals/lobby' },
        { label: 'Statutes', href: '/portals/statutes' },
        { label: 'Campaign Finance', href: '/portals/campaign-finance' },
      ],
    },
    {
      title: 'Tools',
      links: [
        { label: 'Agentic Track', href: '/products/auto-track' },
        { label: 'Advocate Management', href: '/advocate-management' },
        { label: 'AI Bill Brief', href: '/ai-reports' },
        { label: 'Bill Comparison', href: '/products/bill-comparison' },
        { label: 'Live Alerts', href: '/live-actions' },
      ],
    },
    {
      title: 'Solutions',
      links: [
        { label: 'For Lobbyists', href: '/solutions/lobbyists' },
        { label: 'For Associations', href: '/solutions/associations' },
        { label: 'For Corporations', href: '/solutions/corporations' },
      ],
    },
    {
      title: 'Company',
      links: [
        { label: 'About', href: '/about' },
        { label: 'Why LegisIQ', href: '/why-legisiq' },
        { label: 'Blog', href: '/blog' },
        { label: 'Help', href: '/help' },
      ],
    },
  ];

  return (
    <footer className="bg-gradient-to-b from-white to-slate-50 py-8 md:py-16 px-4 md:px-20">
      <div className="container mx-auto">
        <div className="flex flex-wrap md:flex-nowrap gap-10 justify-center">
          {/* Logo and Description */}
          <div className="flex flex-wrap md:flex-nowrap gap-6">
            <div className="md:max-w-[320px] md:me-[200px]">
              <div className="flex items-center justify-center space-x-2 mb-4">
                <Link href="/">
                  <img
                    src="/legisiq_white_transparent.png"
                    alt="LegisIQ Logo"
                    className="w-[19.8rem] md:w-[23.8rem] h-auto"
                  />
                </Link>
              </div>
              <p className="text-gray-700 text-base mb-4 text-center">
                Empowering government affairs teams with intelligent legislative tracking and
                analysis.
              </p>
            </div>

            {/* Dynamic Footer Links */}
            {footerLinks.map((group, index) => (
              <div
                key={index}
                className={`${index === 0 ? 'mr-0 md:mr-12 md:w-[144px]' : 'md:w-[144px]'}`}
              >
                <h3 className="font-bold text-gray-900 mb-4 text-sm uppercase tracking-wide">
                  {group.title}
                </h3>
                <ul className="space-y-2">
                  {group.links.map((link, i) => (
                    <li key={i}>
                      <Link
                        href={link.href}
                        className="text-gray-600 text-base hover:text-[#102A43]"
                      >
                        {link.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <div className="flex-1 md:ms-2 min-w-[300px] max-w-[400px]">
            <h3 className="font-semibold text-gray-600 mb-4 text-sm">Stay up to date</h3>
            <div className="flex items-center gap-4">
              <Input
                placeholder="Enter your email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="bg-transparent border placeholder:text-gray-400 placeholder:font-medium border-gray-300 flex-1 min-w-0"
              />
              <Button
                size="lg"
                className="bg-[#102A43] hover:bg-[#0d1f32] text-white font-semibold"
                onClick={async () => {
                  try {
                    await subscribeNewsletter({ subscribeNewsletterRequest: { email } });
                    toast({ description: 'Subscribed!' });
                    setEmail('');
                  } catch {
                    toast({ description: 'Subscription failed' });
                  }
                }}
              >
                Subscribe
              </Button>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-gray-200 mt-12 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-[#717680] text-base text-center md:text-left">
            © 2025 LegisIQ. All rights reserved.
          </p>
          <div className="flex items-center justify-center space-x-6">
            <Link
              href="/termsandprivacy"
              className="text-gray-600 hover:text-[#102A43] text-base font-medium"
            >
              Terms & Privacy
            </Link>
            {socialIcons.map((icon, index) => (
              <a
                href={icon.href}
                key={index}
                target={icon.href.startsWith('http') ? '_blank' : undefined}
                rel={icon.href.startsWith('http') ? 'noopener noreferrer' : undefined}
                className="text-gray-600 hover:text-[#102A43]"
              >
                <icon.src />
              </a>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
}

export default SiteFooter;
